import { ref, computed } from 'vue'
import { authorizationService } from '@/services/authorizationService'
import { roleService } from '@/services/roleService'
import type { PermissionsSummary, ResourceType, OperationType, RoleDto } from '@/types/roles'
import { 
  canPerformOperation, 
  getAccessibleResources,
  getAllowedOperations,
  isReadOnly,
  hasFullAccess
} from '@/utils/permissions'

/**
 * Global permission state - shared across all instances of usePermissions
 */
const permissionsSummary = ref<PermissionsSummary | null>(null)
const isLoadingPermissions = ref(false)
const permissionsError = ref<Error | null>(null)

/**
 * Global roles cache - shared across all instances
 */
const rolesCache = ref<RoleDto[] | null>(null)
const isLoadingRoles = ref(false)
const rolesError = ref<Error | null>(null)

/**
 * Permission management composable
 * 
 * Provides reactive access to current user's roles, scopes, and permission checking methods.
 * Integrates with backend AuthorizationController and client-side permission utilities.
 * 
 * @example
 * ```ts
 * const { scopes, hasPermission, canWrite, loadPermissions } = usePermissions()
 * 
 * // Load user permissions on mount
 * await loadPermissions()
 * 
 * // Check specific permissions
 * if (hasPermission('presets', 'write')) {
 *   // User can write to presets
 * }
 * 
 * // Check path-based permissions
 * if (canWritePath('tenants', 'Licensing')) {
 *   // User can write to Licensing property
 * }
 * ```
 */
export function usePermissions() {
  /**
   * Load available roles from the backend
   * This caches all roles for role name lookups
   * Note: This endpoint is anonymous and can be called before authentication
   */
  async function loadRoles(): Promise<void> {
    if (isLoadingRoles.value || rolesCache.value) return
    
    isLoadingRoles.value = true
    rolesError.value = null
    
    try {
      const roles = await roleService.getRoles()
      rolesCache.value = roles
      console.log('[usePermissions] Loaded roles:', roles.map(r => `${r.name} (ID: ${r.id})`).join(', '))
    } catch (error) {
      console.error('[usePermissions] Failed to load roles:', error)
      rolesError.value = error as Error
      rolesCache.value = [] // Empty array on error
    } finally {
      isLoadingRoles.value = false
    }
  }

  /**
   * Load current user's permissions from backend
   */
  async function loadPermissions(): Promise<void> {
    if (isLoadingPermissions.value) return
    
    isLoadingPermissions.value = true
    permissionsError.value = null
    
    try {
      // Load roles first (anonymous endpoint, loads in parallel)
      // Don't await - let it load in background
      if (!rolesCache.value) {
        loadRoles().catch(err => {
          console.warn('[usePermissions] Failed to load roles cache:', err)
        })
      }
      
      const summary = await authorizationService.getCurrentUserPermissions()
      permissionsSummary.value = summary
    } catch (error) {
      console.error('[usePermissions] Failed to load permissions:', error)
      permissionsError.value = error as Error
      // Set minimal permissions on error (viewer-like)
      permissionsSummary.value = {
        identifier: '',
        authenticationType: 'SubscriptionKey',
        roles: [],
        scopes: [],
        isAuthenticated: false
      }
    } finally {
      isLoadingPermissions.value = false
    }
  }

  /**
   * Refresh permissions (useful after role changes)
   */
  async function refreshPermissions(): Promise<void> {
    permissionsSummary.value = null
    await loadPermissions()
  }

  function resetPermissions(): void {
    permissionsSummary.value = null
    permissionsError.value = null
    isLoadingPermissions.value = false
  }

  /**
   * Current user's roles (array of RoleDto objects)
   */
  const roles = computed(() => permissionsSummary.value?.roles || [])

  /**
   * Current user's role IDs (array of role IDs extracted from RoleDto objects)
   */
  const roleIds = computed(() => roles.value.map(r => r.id))

  /**
   * Current user's role names (array of role names extracted from RoleDto objects)
   */
  const roleNames = computed(() => roles.value.map(r => r.name))

  /**
   * Current user's combined scopes from all roles
   */
  const scopes = computed(() => permissionsSummary.value?.scopes || [])

  /**
   * Check if user has a specific permission
   * 
   * @param resource - Resource type (e.g., 'presets', 'tenants')
   * @param operation - Operation type (e.g., 'read', 'write')
   * @param path - Optional path within resource (e.g., 'Licensing', 'database.*')
   * @returns True if user has permission
   */
  function hasPermission(
    resource: ResourceType,
    operation: OperationType,
    path?: string
  ): boolean {
    if (!permissionsSummary.value) return false
    return canPerformOperation(scopes.value, resource, operation, path)
  }

  /**
   * Check if user can write to a specific path
   * Convenience wrapper for hasPermission(resource, 'write', path)
   */
  function canWritePath(resource: ResourceType, path: string): boolean {
    return hasPermission(resource, 'write', path)
  }

  /**
   * Check if user can read a resource
   */
  function canRead(resource: ResourceType): boolean {
    return hasPermission(resource, 'read')
  }

  /**
   * Check if user can write to a resource
   */
  function canWrite(resource: ResourceType): boolean {
    return hasPermission(resource, 'write')
  }

  /**
   * Check if user can create a resource
   */
  function canCreate(resource: ResourceType): boolean {
    return hasPermission(resource, 'create')
  }

  /**
   * Check if user can delete a resource
   */
  function canDelete(resource: ResourceType): boolean {
    return hasPermission(resource, 'delete')
  }

  /**
   * Get list of resources user can access with specific operation
   * 
   * @param operation - Operation type to check
   * @returns Array of accessible resource types
   */
  function getAccessibleResourcesForOperation(operation: OperationType): ResourceType[] {
    if (!permissionsSummary.value) return []
    return getAccessibleResources(scopes.value, operation) as ResourceType[]
  }

  /**
   * Get list of operations allowed on a specific resource
   * 
   * @param resource - Resource type to check
   * @param path - Optional path within resource
   * @returns Array of allowed operation types
   */
  function getAllowedOperationsForResource(
    resource: ResourceType,
    path?: string
  ): OperationType[] {
    if (!permissionsSummary.value) return []
    return getAllowedOperations(scopes.value, resource, path) as OperationType[]
  }

  /**
   * Check if user has read-only access to a resource
   * (can read but cannot write, create, or delete)
   */
  function isReadOnlyAccess(resource: ResourceType): boolean {
    if (!permissionsSummary.value) return true
    return isReadOnly(scopes.value, resource)
  }

  /**
   * Check if user has full access to a resource
   * (can perform all operations: read, write, create, delete)
   */
  function hasFullAccessToResource(resource: ResourceType): boolean {
    if (!permissionsSummary.value) return false
    return hasFullAccess(scopes.value, resource)
  }

  /**
   * Get role ID by name from cache
   */
  function getRoleIdByName(roleName: string): number | undefined {
    const role = rolesCache.value?.find(r => r.name.toLowerCase() === roleName.toLowerCase())
    return role?.id
  }

  /**
   * Check if user has a specific role by ID
   */
  function hasRole(roleId: number): boolean {
    return roleIds.value.includes(roleId)
  }

  /**
   * Check if user has a specific role by name
   * Falls back to checking user's actual role names if roles cache isn't loaded yet
   */
  function hasRoleByName(roleName: string): boolean {
    // Try to use cached roles first (most reliable)
    if (rolesCache.value && rolesCache.value.length > 0) {
      const roleId = getRoleIdByName(roleName)
      return roleId !== undefined && hasRole(roleId)
    }
    
    // Fallback: check user's actual role names directly
    // This works even if the roles cache hasn't loaded yet
    const normalizedName = roleName.toLowerCase()
    return roleNames.value.some(name => name.toLowerCase() === normalizedName)
  }

  /**
   * Check if user has any of the specified roles
   */
  function hasAnyRole(roleIdList: number[]): boolean {
    return roleIdList.some(id => roleIds.value.includes(id))
  }

  /**
   * Check if user has all of the specified roles
   */
  function hasAllRoles(roleIdList: number[]): boolean {
    return roleIdList.every(id => roleIds.value.includes(id))
  }

  /**
   * Check if permissions are loaded
   */
  const isLoaded = computed(() => permissionsSummary.value !== null && !isLoadingPermissions.value)

  /**
   * Check if user has administrator role (dynamic lookup by name)
   */
  const isAdministrator = computed(() => hasRoleByName('Administrator'))

  /**
   * Check if user has identity admin role (dynamic lookup by name)
   */
  const isIdentityAdmin = computed(() => hasRoleByName('Identity Admin'))

  /**
   * Check if user has contributor role (dynamic lookup by name)
   */
  const isContributor = computed(() => hasRoleByName('Contributor'))

  /**
   * Check if user has viewer role (dynamic lookup by name)
   */
  const isViewer = computed(() => hasRoleByName('Viewer'))

  /**
   * Check if user can edit configurations (has write access to presets or tenants)
   */
  const canEditConfigurations = computed(() => 
    canWrite('presets') || canWrite('tenants')
  )

  return {
    // State
    permissionsSummary: computed(() => permissionsSummary.value),
    roles,
    roleIds,
    roleNames,
    scopes,
    isLoading: computed(() => isLoadingPermissions.value),
    isLoaded,
    error: computed(() => permissionsError.value),

    // Roles cache
    availableRoles: computed(() => rolesCache.value || []),
    isLoadingRoles: computed(() => isLoadingRoles.value),
    rolesError: computed(() => rolesError.value),

    // Role checks
    hasRole,
    hasRoleByName,
    getRoleIdByName,
    hasAnyRole,
    hasAllRoles,
    isAdministrator,
    isIdentityAdmin,
    isContributor,
    isViewer,

    // Permission checks
    hasPermission,
    canRead,
    canWrite,
    canCreate,
    canDelete,
    canWritePath,
    canEditConfigurations,

    // Advanced queries
    getAccessibleResourcesForOperation,
    getAllowedOperationsForResource,
    isReadOnlyAccess,
    hasFullAccessToResource,

    // Actions
    loadPermissions,
    loadRoles,
    refreshPermissions,
    resetPermissions
  }
}
